import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bot, Loader2, Check } from 'lucide-react';
import { getAuthHeaders } from '../../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Grid of available AI Task Agents. Admin picks one to wire to the task.
 * Reads GET /api/admin/agent-tasks/ai-agents (registry in the backend).
 */
export default function AiAgentPicker({ value, onChange, isAr = true }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/agent-tasks/ai-agents`, {
          headers: getAuthHeaders(),
        });
        if (alive) setAgents(res.data?.data || []);
      } catch {
        // silent — parent form still works, just no agents listed
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 text-white/50">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (agents.length === 0) {
    return (
      <p className="text-xs text-white/50 py-2">
        {isAr ? 'لا يوجد وكلاء AI مسجّلون بعد.' : 'No AI agents registered yet.'}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ai-agent-picker">
      {agents.map((a) => {
        const selected = value === a.id;
        return (
          <button
            key={a.id}
            type="button"
            data-testid={`ai-agent-${a.id}`}
            onClick={() => onChange(a.id)}
            className={`relative text-start p-3 rounded-lg border transition-all ${
              selected
                ? 'bg-purple-500/15 border-purple-400 ring-1 ring-purple-400/40'
                : 'bg-white/[0.02] border-white/10 hover:border-white/25'
            }`}
          >
            {selected && (
              <Check className="absolute top-2 end-2 h-4 w-4 text-purple-300" />
            )}
            <div className="flex items-center gap-2 mb-1">
              <Bot className={`h-4 w-4 ${selected ? 'text-purple-300' : 'text-white/50'}`} />
              <span className="text-sm text-white font-medium">
                {isAr ? a.label_ar : a.label_en}
              </span>
            </div>
            <p className="text-[11px] text-white/50 leading-relaxed">
              {isAr ? a.descAr : a.descEn}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(a.output_shape || []).slice(0, 4).map((f) => (
                <span key={f} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/40 border border-white/10">
                  {f}
                </span>
              ))}
              {(a.output_shape || []).length > 4 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/40 border border-white/10">
                  +{a.output_shape.length - 4}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
